import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import TemplateCard from './TemplateCard';
import TemplateFilters from './TemplateFilters';
import '../styles/Jobs.css';

function Jobs() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([499, 19900]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('price_desc');

  // Mobile filter panel
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        api.get('/api/templates?limit=100'),
        api.get('/api/templates/categories'),
      ]);
      setTemplates(templatesRes.templates || []);
      setCategories(categoriesRes || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and sorting client-side
  const filteredTemplates = useMemo(() => {
    let result = [...templates];

    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter((t) => selectedCategories.includes(t.category));
    }

    // Price filter
    result = result.filter(
      (t) => t.price_cents >= priceRange[0] && t.price_cents <= priceRange[1]
    );

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          (t.description && t.description.toLowerCase().includes(query))
      );
    }

    // Sorting
    switch (sortBy) {
      case 'price_asc':
        result.sort((a, b) => a.price_cents - b.price_cents);
        break;
      case 'price_desc':
        result.sort((a, b) => b.price_cents - a.price_cents);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'popular':
        // Keep default order (could be enhanced with actual popularity data)
        break;
      default:
        break;
    }

    return result;
  }, [templates, selectedCategories, priceRange, searchQuery, sortBy]);

  if (loading) {
    return (
      <div className="jobs-page">
        <div className="jobs-loading">
          <div className="loading-spinner"></div>
          <p>Loading services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="jobs-page">
        <div className="jobs-error">
          <p>{error}</p>
          <button onClick={loadData} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="jobs-page">
      {/* Header */}
      <header className="jobs-header">
        <div className="jobs-header-content">
          <h1 className="jobs-title">
            <span className="gradient-text">Services</span> to Build Your Business
          </h1>
          <p className="jobs-subtitle">
            We cut to the facts and guide you to exactly what you need.
          </p>
        </div>
      </header>

      {/* Mobile Filter Toggle */}
      <button
        className="mobile-filter-toggle"
        onClick={() => setShowFilters(!showFilters)}
      >
        {showFilters ? '✕ Hide Filters' : '☰ Show Filters'}
      </button>

      {/* Main Content */}
      <div className="jobs-content">
        {/* Filters Sidebar */}
        <div className={`filters-container ${showFilters ? 'show' : ''}`}>
          <TemplateFilters
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryChange={setSelectedCategories}
            priceRange={priceRange}
            onPriceRangeChange={setPriceRange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            totalCount={filteredTemplates.length}
          />
        </div>

        {/* Template Grid */}
        <div className="templates-container">
          {filteredTemplates.length === 0 ? (
            <div className="no-results">
              <p>No services match your filters.</p>
              <button
                onClick={() => {
                  setSelectedCategories([]);
                  setSearchQuery('');
                  setPriceRange([499, 19900]);
                }}
                className="btn btn-ghost"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="templates-grid">
              {filteredTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Jobs;
